// Orchestrator for MeowFi analytics. This file now delegates to modular services
// for GraphQL fetching, pricing, on-chain state, and analytics processing. The
// public API and functionality remain unchanged.

import { fetchAllSmartWallets, fetchWalletsWithPositions } from './services/graphqlService';
import { getAllTokenPrices } from './services/priceService';
import { fetchAllPositions, getPoolState } from './services/blockchainService';
import { collectAllPositions, processPositions } from './services/analyticsService';

export async function runMeowFiAnalytics() {
  // Step 1-3: Fetch wallet count, prices, and wallets-with-positions in parallel
  console.time('Phase A: initial fetches (count, prices, wallets-with-positions)');
  const [allWallets, allPrices, wallets] = await Promise.all([
    fetchAllSmartWallets(),
    getAllTokenPrices(),
    fetchWalletsWithPositions(),
  ]);
  const totalSmartWallets = allWallets.length;
  console.timeEnd('Phase A: initial fetches (count, prices, wallets-with-positions)');

  // Step 4: Collect positions/pools
  console.time('Phase B: collect positions & pools');
  const { allNftIds, allPoolAddresses } = collectAllPositions(wallets);
  console.timeEnd('Phase B: collect positions & pools');

  // Step 5: Fetch positions
  console.time('Phase C: fetch positions');
  const positionsData = await fetchAllPositions(allNftIds);
  console.timeEnd('Phase C: fetch positions');

  // Step 6: Derive only the pools we actually need states for (positions that fetched OK)
  const usedPoolSet = new Set();
  for (const w of wallets) {
    for (const p of w.positions) {
      if (positionsData[p.nftId]) {
        usedPoolSet.add(p.vault.pool);
      }
    }
  }
  const targetPoolAddresses = usedPoolSet.size > 0 ? Array.from(usedPoolSet) : allPoolAddresses;

  // Step 6b: Fetch pool states for target pools only
  console.time('Phase D: fetch pool states');
  const poolStates = {};
  const poolStateBatchSize = 12; // bump concurrency further
  for (let i = 0; i < targetPoolAddresses.length; i += poolStateBatchSize) {
    const batch = targetPoolAddresses.slice(i, i + poolStateBatchSize);
    const results = await Promise.all(batch.map((addr) => getPoolState(addr)));
    results.forEach((ps, idx) => {
      if (ps) poolStates[batch[idx]] = ps;
    });
    if (i + poolStateBatchSize < targetPoolAddresses.length) {
      await new Promise((r) => setTimeout(r, 150)); // lighter backoff
    }
  }
  console.timeEnd('Phase D: fetch pool states');

  // Step 7: Process
  console.time('Phase E: process analytics');
  const results = await processPositions(wallets, positionsData, poolStates, allPrices);
  console.timeEnd('Phase E: process analytics');

  // Active vs inactive TVL
  const totalTVL = results.totalValueFromQualifiedWallets;
  const inactiveTVL = results.totalInactiveValueFromQualifiedWallets || 0;
  const activeTVL = Math.max(totalTVL - inactiveTVL, 0);
  const activeTVLPercent = totalTVL > 0 ? (activeTVL / totalTVL) * 100 : 0;

  const averagePositionValueFromQualifiedWallets =
    results.totalPositionsFromQualifiedWallets > 0
      ? results.totalValueFromQualifiedWallets / results.totalPositionsFromQualifiedWallets
      : 0;

  return {
    totalSmartWallets,
    walletsWithActivePositions: wallets.length,
    walletsWithPositionsAbove10: results.walletsWithPositionsAbove10,
    totalPositionsAbove10: results.totalPositionsAbove10,
    totalPositionsFromQualifiedWallets: results.totalPositionsFromQualifiedWallets,
    averagePositionValueFromQualifiedWallets,
    totalValueFromQualifiedWallets: results.totalValueFromQualifiedWallets,

    // NEW
    totalTVL,
    activeTVL,
    activeTVLPercent, // % of TVL earning fees
    totalInactiveValueFromQualifiedWallets: results.totalInactiveValueFromQualifiedWallets,

    qualifyingWallets: results.aboveThresholdWallets,
    positionsFetched: Object.keys(positionsData).length,
    totalPositionsRequested: allNftIds.length,
    poolStatesValid: Object.keys(poolStates).length,
    totalPoolsRequested: allPoolAddresses.length,
  };
}
